import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

const FORUM_BASE = 'https://en.forum.saysomethingin.com';

// /c/welsh/meetups-events/12 → "Meetups & Events"
// /t/some-thread-slug/123    → "Discussion"
function pathToLabel(path) {
  if (!path || path === '/' || path === '') return 'Latest';
  const segs = path.replace(/^\/|\/$/g, '').split('/');
  if (segs[0] === 'c') {
    const last = segs[segs.length - 2] || segs[segs.length - 1];
    return last
      .replace(/-/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase())
      .replace(/ And /g, ' & ');
  }
  if (segs[0] === 't') return 'Discussion';
  return 'Forum';
}

// `path` comes straight from the query string, so treat it as hostile.
// Resolve it against the forum origin and return it only if it stays there
// over https. A bare concat let ?path=@evil.com, //evil.com, https://evil.com
// frame an attacker's site under the SSi header. The https guard also blocks
// blob:/data: URLs that share the forum origin. Fall back to the root.
function safeForumUrl(rawPath) {
  try {
    const url = new URL(rawPath, FORUM_BASE + '/');
    if (url.origin === FORUM_BASE && url.protocol === 'https:') return url.href;
  } catch {
    /* malformed path – fall through to the safe root */
  }
  return FORUM_BASE + '/';
}

export function ForumEmbed() {
  const [params] = useSearchParams();
  const rawPath = params.get('path') || '/';
  const fullUrl = safeForumUrl(rawPath);
  // Label from the validated pathname, so a rejected off-origin path can't
  // leak attacker text into the breadcrumb either.
  const label = pathToLabel(new URL(fullUrl).pathname);
  const [showFallback, setShowFallback] = useState(false);
  const timerRef = useRef(null);

  // If the iframe never fires `load` within 4s, surface the fallback callout.
  // Cross-origin means we can't inspect what the iframe rendered — a missed
  // load is the strongest signal we have that X-Frame-Options blocked us.
  // The iframe's onLoad cancels this timer so the fallback can't appear
  // over a successfully-loaded forum.
  useEffect(() => {
    setShowFallback(false);
    timerRef.current = setTimeout(() => setShowFallback(true), 4000);
    return () => clearTimeout(timerRef.current);
  }, [fullUrl]);

  return (
    <div style={feStyles.wrap} data-screen-label="Forum">
      <div style={feStyles.strip} className="ssi-forum__strip">
        <div style={feStyles.bc}>
          <Link to="/community" style={feStyles.bcCrumb}>Community</Link>
          <span style={feStyles.bcSep} aria-hidden="true">›</span>
          <span style={feStyles.bcCurrent}>SSi Forum</span>
          <span style={feStyles.bcDot} aria-hidden="true">·</span>
          <span style={feStyles.bcPath}>{label}</span>
        </div>
        <div style={feStyles.bcActions}>
          <span style={feStyles.bcFree}>Free to join</span>
          <a
            href={fullUrl}
            target="_blank"
            rel="noopener"
            style={feStyles.bcOpen}>
            Open in new tab
            <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.8" style={{marginLeft:6, verticalAlign:'middle'}}>
              <path d="M6 3H3v10h10v-3"/>
              <path d="M9 2h5v5"/>
              <path d="M8 8l6-6"/>
            </svg>
          </a>
        </div>
      </div>
      <div style={feStyles.frameWrap} className="ssi-forum__frameWrap">
        <iframe
          src={fullUrl}
          title="SSi Forum"
          style={feStyles.frame}
          referrerPolicy="no-referrer-when-downgrade"
          onLoad={() => {
            clearTimeout(timerRef.current);
            setShowFallback(false);
          }}
        />
        {showFallback && (
          <div style={feStyles.fallback} className="ssi-forum__fallback">
            <div style={feStyles.fallbackCard}>
              <div style={feStyles.fallbackKicker}>If the forum hasn't loaded</div>
              <h2 style={feStyles.fallbackH}>The forum may be blocked from embedding.</h2>
              <p style={feStyles.fallbackP}>
                Discourse refuses to embed by default. Once the forum admin allows
                <code style={feStyles.fallbackCode}>saysomethingin.com</code>
                as a frame ancestor, it will appear here. In the meantime –
              </p>
              <a href={fullUrl} target="_blank" rel="noopener" className="btn btn--primary">
                Open the forum in a new tab
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const feStyles = {
  wrap: { display:'flex', flexDirection:'column', flex:'1 1 auto', minHeight:0, background:'#fff' },
  strip: {
    display:'flex', alignItems:'center', justifyContent:'space-between',
    gap:16, padding:'12px 32px', background:'var(--offWhite)',
    borderBottom:'1px solid var(--border)',
    fontSize:13, color:'var(--grey)',
  },
  bc: { display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', minWidth:0 },
  bcCrumb: { color:'var(--grey)', textDecoration:'none', fontWeight:500 },
  bcCurrent: { color:'var(--fg-1)', fontWeight:600 },
  bcSep: { color:'var(--border-strong)', opacity:.6 },
  bcDot: { color:'var(--border-strong)', opacity:.6, marginLeft:4 },
  bcPath: { color:'var(--grey)', fontStyle:'italic' },
  bcActions: { display:'flex', alignItems:'center', gap:14 },
  bcFree: {
    fontSize:11, letterSpacing:'.12em', textTransform:'uppercase',
    fontWeight:600, color:'var(--brandRed)',
    padding:'4px 10px', background:'var(--pastelRed)', borderRadius:999,
  },
  bcOpen: {
    fontSize:13, fontWeight:500, color:'var(--fg-1)',
    textDecoration:'none', display:'inline-flex', alignItems:'center',
  },
  frameWrap: { position:'relative', flex:'1 1 auto', minHeight:480, background:'#fff' },
  // iframe height:100% doesn't resolve through a flex-sized parent, so anchor
  // the iframe to the wrapper's edges via absolute positioning instead.
  frame: { position:'absolute', inset:0, width:'100%', height:'100%', border:0, display:'block' },
  fallback: {
    position:'absolute', inset:0, display:'flex',
    alignItems:'center', justifyContent:'center',
    background:'rgba(253,245,245,.96)',
    pointerEvents:'none',
  },
  fallbackCard: {
    pointerEvents:'auto',
    maxWidth:520, padding:'32px 36px',
    background:'#fff', border:'1px solid var(--border)',
    borderRadius:16, boxShadow:'var(--shadow-md)',
    textAlign:'left',
  },
  fallbackKicker: { fontSize:11, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:600, marginBottom:10 },
  fallbackH: { fontFamily:'var(--font-display)', fontSize:24, lineHeight:1.2, margin:'0 0 12px', color:'var(--fg-1)' },
  fallbackP: { fontSize:15, lineHeight:1.5, color:'var(--grey)', margin:'0 0 20px' },
  fallbackCode: {
    background:'var(--bg-soft)', padding:'2px 8px', borderRadius:6,
    fontFamily:'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize:13, margin:'0 4px', color:'var(--deepRed)',
  },
};
